// Phase 43 — <CanvasHeroIngest /> (Plan 43-05 Task 1, CANVAS-01).
// Phase 45 — DOC-06 wiring (Plan 45-08): the "Upload factory packet" card is
// ENABLED and supports drag-and-drop (drop files onto it OR click to browse).
// Phase 45.5 (D-01): the marketplace-URL lane (INGEST-02) was CUT, so the URL
// card is removed entirely. The Enter-HIN / walkaround-video cards are
// TEMPORARILY hidden (commented out) per request — restore them when their
// Phase-46 lanes ship.
//
// Phase 45.5.1 — CANVAS-08 (D-1 + D-2): the hero now commits the dropped/
// browsed packet INLINE with NO CombinedUploadReview modal. The drop/browse
// path runs a single sequence: ensureDraft() (silently create a condition='new'
// Draft when none exists — D-2) → upload.onDrop(files) → upload.commit() (POST
// /boats/{id}/combined-upload) → onConfirmExtract(boatId) (auto-fire extraction,
// NO confirm gate — O-3). The dealer's visibility + correction surface is the
// inline <UploadedFilesStrip /> (Plan 01) rendered from upload.items, NOT a
// blocking dialog.
//
// SAFETY BOUNDARY (D-1): removing the modal does NOT weaken the public/private
// gate. The boundary stays SERVER-AUTHORITATIVE in two untouched places — the
// combined-upload endpoint (InventoryCombinedUploadController re-enforces each
// item's final bucket regardless of client payload) and the packet-router AI
// clampToPrivateDocument (Phase 45.5 Plan 02, D-05). The modal was a review
// surface, never the enforcement layer. The strip's "Move" control is the
// PRE-COMMIT client reroute; a dealer-facing POST-COMMIT cross-bucket move is a
// documented fast-follow (O-1) — there is no REST verb for it yet, so we do NOT
// fabricate one and do NOT block this flow on it.
//
// LIFTED upload (Plan 02): this component no longer constructs its own
// useCombinedUpload — LiveFillCanvas owns the single instance (bound to the live
// draftSync.boatId) and passes it (+ ensureDraft) down as props, so commit()
// posts to the boat ensureDraft just created.
//
// Blank-canvas hero ingest prompt rendered ONLY on `?new` without `resume=`
// per UI-SPEC §"Hero ingest prompt" (D-04). The canvas is the sole add-boat
// surface, so the hero renders for every tenant (DOC-06, no flag).
//
// Phase 45.5.3 (refresh) — the hero is a SINGLE refined factory-packet dropzone
// (the deferred HIN / walkaround-video cards and their disabled-tooltip lane
// were removed: HERO_CARDS had one enabled entry so the disabled `else` branch
// was unreachable dead code — review IN-01). The dropzone is the SOLE add path
// (the separate "Add more" control was removed): it shows PROMINENT in the empty
// state and persists in a COMPACT form above the media grid once files exist /
// after the first commit / while a packet is being read — it is never fully
// dismissed or replaced by the reading status (the reading status renders
// alongside the compact dropzone, which stays a usable drop target).
//
// Safety boundary (DOC-06): the factory-packet dropzone only stages + commits
// via the existing runInlineIngest flow — all public/private bucket routing +
// the server-side combined-upload endpoint enforce the safety gate (45-06).
// This adds no new routing path (T-45-01e).
//
// Typography lock (UI-SPEC rev 2): the disallowed 12px size utility and
// 500-weight class names appear nowhere in this file. Headline 20px semibold
// ("Display"); body 14px regular ("Body").
//
// Dismiss state (repurposed for the persistent dropzone):
// - "Skip — I'll fill manually" no longer HIDES the hero (item 5: the dropzone
//   must stay available as the sole add path). It COLLAPSES the verbose
//   "Start your listing" intro into the same COMPACT dropzone the with-files
//   state shows, so the drop target is never lost — no dead state.
// - For drafts with a real draftId: per-draftId localStorage remembers that
//   collapse decision (NOT cross-draft).
// - For blank-new (draftId == null): React state ONLY — closing the wizard and
//   starting another new boat shows the full intro again. Persisting the
//   blank-new collapse across sessions stuck users with no in-app recovery.

import { useRef, useState } from 'react';
import {
  AlertCircle,
  UploadCloud,
  X,
} from 'lucide-react';
import { Button } from '@marine/ui/button';
import { cn } from '@/lib/utils';
// Phase 45.5.3 (CANVAS-15) — the v1-style MediaCard grid REPLACES the flat
// UploadedFilesStrip list as the dealer's visibility + pre-commit correction
// surface (image/PDF thumbnails + ⭐). UploadedFilesStrip is preserved on disk
// (D-15 / CLAUDE.md: removal is reversible) but no longer rendered here.
// `useCombinedUpload` + `TempMediaItem` are imported TYPE-ONLY: LiveFillCanvas
// owns the single combined-upload instance + the committed gallery and passes
// them down (this component constructs no hook of its own).
import { MediaCard } from './MediaCard';
import { SortHeldUploadsButton } from '../media/PendingImageActions';
import type {
  useCombinedUpload,
  CombinedUploadResult,
} from '../documents/hooks/useCombinedUpload';
import type { TempMediaItem } from './types';
// Phase 45.5.3 (committed documents on resume) — the factory-packet document
// row shape. Committed DOCUMENTS live in a separate store from committed images
// (BoatMediaResponse is { images, videos } — no documents), so LiveFillCanvas
// sources them from useDocuments(boatId) and threads them through here. Type-only.
import type { BoatDocument } from '../documents/hooks/useDocuments';
import type { PendingBoatImage } from '@/modules/inventory/services/boatMediaApi';

/** The lifted combined-upload instance LiveFillCanvas passes to the hero. */
type CombinedUpload = ReturnType<typeof useCombinedUpload>;

// DOC-06 — the MIME accept-list for the factory-packet picker mirrors the
// server-side combined-upload accept list (45-06): images go to the public
// bucket, PDF/Office to the private bucket; the review screen is the per-item
// re-route + confirm gate.
const FACTORY_PACKET_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

interface CanvasHeroIngestProps {
  /** Scopes the localStorage dismiss key. `null` for blank-new (no draft id yet). */
  draftId: string | null;
  /**
   * Phase 45.5.1 (CANVAS-08) — the LIFTED combined-upload instance owned by
   * LiveFillCanvas (bound to the live draftSync.boatId). The hero stages onto it
   * (onDrop), commits it (commit), and renders its `items` in the inline strip.
   */
  upload: CombinedUpload;
  /**
   * Phase 45.5.1 (CANVAS-08, D-2) — silently ensure a saved draft exists before
   * commit. Returns the boat id (existing or freshly auto-created condition='new'),
   * or null if creation failed (the caller then fail-soft skips commit/extract;
   * ensureDraft has already surfaced a toast).
   */
  ensureDraft: () => Promise<string | null>;
  /**
   * Phase 45.5 (D-17) — fires after a successful inline commit with the saved
   * boat id, handing off to the canvas reading state. The canvas (LiveFillCanvas)
   * wires this to useFactoryPacketExtract.trigger (auto-fire, no confirm gate).
   */
  onConfirmExtract?: (boatId: string) => void;
  /**
   * Phase 45.5.3 (review) — fires after a successful inline commit (the staged
   * combined-upload POST) with the saved boat id, INDEPENDENT of extraction.
   * #1219 — a canvas commit holds image uploads in `inventory_pending_images`
   * until the classifier (or the dealer's "Use as photo") promotes them, so the
   * host refreshes both the gallery (images + pending_images) and the documents.
   * Distinct from onConfirmExtract: a packet may commit images
   * (which the grid must show) even on a documents-only extraction hand-off, and
   * the media grid must refresh whether or not extraction runs.
   *
   * The commit `result` is passed too: after useCombinedUpload.commit() clears
   * the staged items (the staged↔committed de-dup fix), the query refetch is
   * async, so a first drop would briefly render NO cards (the grid returns null
   * when both sources are empty). The canvas seeds the boat-media cache from
   * `result.images` so the committed cards are present the instant staged clears,
   * eliminating the flash; the background invalidate then reconciles to
   * card-size conversions.
   */
  onCommitted?: (boatId: string, result?: CombinedUploadResult) => void;
  /**
   * Phase 45.5 — true while the packet is being read; surfaces the "now reading
   * your documents…" transition copy in place of the hero cards.
   */
  isReading?: boolean;
  /**
   * Phase 45.5.3 (CANVAS-15) — the COMMITTED gallery media, derived in
   * LiveFillCanvas from the live useBoatMedia(boatId) query (the SINGLE source of
   * truth) and mapped into the committed MediaCard shape. Saved images render in
   * the SAME grid as staged items and can be ⭐ set-featured / deleted. Because
   * the list is query-backed, a delete / set-primary mutation invalidates the
   * query and this prop updates on the refetch — the grid self-heals (no local
   * reconciliation). Defaults to `[]` (the grid then shows staged items only).
   */
  committedItems?: TempMediaItem[];
  /**
   * Phase 45.5.3 — the server mediaId of the gallery's PRIMARY image. Per
   * BoatMediaManager the primary is `images[0]`, so LiveFillCanvas passes
   * `images[0]?.id`. The committed card whose `mediaId` matches renders the ⭐
   * Featured badge. mediaId-matched (not index-based) so a refetch that reorders
   * the list still flags the correct card. Undefined → no committed primary.
   */
  committedPrimaryImageId?: number;
  /**
   * Phase 45.5.3 (committed documents on resume) — the COMMITTED factory-packet
   * documents (COO / invoice / BOL …), derived in LiveFillCanvas from the live
   * useDocuments(boatId) query (the SINGLE source of truth, mirroring committed
   * images). These render as `kind: 'committed-document'` MediaCards in the SAME
   * grid as staged items + committed images so a resumed draft's uploaded
   * documents are VISIBLE (the original bug: BoatMediaResponse has no documents,
   * so the canvas never showed them). Query-backed, so a delete invalidates
   * documentKeys.lists() and the grid self-heals on the refetch. Defaults to [].
   */
  committedDocuments?: BoatDocument[];
  /**
   * #1219 — held AI-intake images from the same useBoatMedia query
   * (`pending_images`): canvas uploads the classifier has not routed yet. They
   * render as private "Not yet classified" cards with an authenticated preview.
   */
  pendingImages?: PendingBoatImage[];
  /**
   * Phase 45.5.3 — delete a committed document. LiveFillCanvas wires this to
   * useDocuments(boatId).destroy.mutate({ mediaId }); its onSuccess invalidates
   * documentKeys.lists() so the query refetches and the deleted card self-heals
   * away — same contract as the committed-image Delete (which the MediaCard
   * fires internally via useDeleteBoatMedia).
   */
  onDeleteDocument?: (mediaId: number) => void;
}

export function CanvasHeroIngest({
  draftId,
  upload,
  ensureDraft,
  onConfirmExtract,
  onCommitted,
  isReading = false,
  committedItems = [],
  committedPrimaryImageId,
  committedDocuments = [],
  onDeleteDocument,
  pendingImages = [],
}: CanvasHeroIngestProps) {
  // Phase 45.5.3 (refresh) — `dismissed` no longer HIDES the hero (the dropzone
  // is the sole add path and must stay available — item 5). It COLLAPSES the
  // verbose "Start your listing" intro into the same compact dropzone the
  // with-files state shows. Persist that collapse per real draftId; blank-new
  // uses React state only so opening another new boat shows the full intro again.
  const storageKey = draftId ? `canvas-hero-dismissed-${draftId}` : null;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  // Drag-over highlight for the factory-packet dropzone.
  const [isDragging, setIsDragging] = useState(false);
  // The SINGLE hidden file picker behind the dropzone (the separate "Add more"
  // picker was removed — the dropzone is now the only add path).
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CANVAS-08 (D-1 + D-2) — the single inline ingest sequence shared by the
  // native drop AND the file-picker. NO review modal: ensureDraft (silently
  // auto-create a condition='new' draft when none exists, giving a boatId) →
  // stage + MIME-split (upload.onDrop) → commit to the server → auto-fire
  // extraction (onConfirmExtract, no confirm gate — O-3). On commit failure we
  // do NOTHING destructive: the staged items stay visible in the strip and
  // upload.commitError surfaces inline so the dealer can retry — we never
  // upload.clear() on error.
  const runInlineIngest = async (files: File[]) => {
    if (files.length === 0) return;
    // Silently ensure a saved draft (gives the boatId commit/extract bind to).
    const boatId = await ensureDraft();
    if (!boatId) return; // fail-soft: ensureDraft already toasted.
    // CR-01/WR-02: stage + commit in the SAME tick. setItems (inside onDrop)
    // and the just-created boatId have NOT flushed into this render's `upload`
    // closure yet, so we must NOT rely on the hook's bound `items`/`boatId`.
    // onDrop RETURNS the freshly-staged batch; pass it AND the fresh boatId
    // straight into commit so it POSTs the dropped files to the new boat
    // instead of an empty batch with a null id.
    const staged = upload.onDrop(files);
    try {
      // #581 Task 9 — this commit does NOT need to pass suppressExtract here:
      // `upload` is the LIFTED instance LiveFillCanvas constructs with
      // suppressExtract=true baked in (see LiveFillCanvas.tsx), so every
      // commit through this closure already omits the backend's auto-trigger
      // extraction — this hero drives extraction itself via
      // onConfirmExtract(boatId) below.
      const result = await upload.commit({ boatId, items: staged });
      // CR-02: only hand off to extraction when something actually committed.
      // A successful-but-empty commit must NOT flip the hero into the
      // irreversible isReading state (which removes the dropzone + Skip),
      // stranding the dealer with nothing uploaded and no recovery path.
      // #1219 — a canvas commit's images come back HELD (pending_images), not in
      // `images`: count them, or a photos-only drop would never be classified.
      const committedCount =
        (result?.images?.length ?? 0) +
        (result?.pending_images?.length ?? 0) +
        (result?.documents?.length ?? 0);
      if (committedCount > 0) {
        // Phase 45.5.3 (review) — refresh the query-backed committed grid FIRST:
        // #1219 — a canvas commit's image items now land HELD in
        // inventory_pending_images, not promoted into inventory_images, until the
        // classifier (or the dealer) routes them. The committed grid is sourced
        // from useBoatMedia(boatId), so invalidating
        // that query (via this callback in LiveFillCanvas) makes the freshly
        // committed images appear. Fired on every non-empty commit, independent
        // of whether extraction runs below (a documents-only packet still needs
        // no media refresh, but an image commit always does — the callback is
        // cheap + idempotent, so we fire on any non-empty commit).
        //
        // The `result` is threaded so the canvas can SEED the boat-media cache
        // synchronously: commit() has just cleared the staged items, so without a
        // seed the grid would flash empty until the async refetch lands.
        onCommitted?.(boatId, result);
        // Auto-fire extraction with the just-saved boat — no confirm gate (O-3).
        // The committed items stay in upload.items so the strip keeps showing
        // their public/private placement while the packet is read.
        onConfirmExtract?.(boatId);
      }
      // else: leave the hero interactive; commitError (if any) shows in the strip.
    } catch {
      // Non-destructive: upload.commitError renders in the strip; staged items
      // remain so the dealer can retry. Do NOT clear here.
    }
  };

  const handleFactoryPacket = () => {
    // WR-01: in-flight guard. Opening the picker while a prior batch is
    // mid-commit could stage + commit a second batch concurrently (commit
    // snapshots its own items and does not serialize), racing two commits to the
    // same boat. Block the picker until the in-flight commit settles.
    if (upload.committing) return;
    // Open the native picker; the inline sequence runs once files are chosen.
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset so picking the same file again re-fires change.
    event.target.value = '';
    // WR-01: guard the picker-selection entry point too (a picker can resolve
    // after a concurrent drop started a commit).
    if (upload.committing) return;
    void runInlineIngest(files);
  };

  // Native drag-and-drop onto the factory-packet card — same destination as the
  // picker: run the inline ingest sequence (auto-split by MIME, safe-default to
  // the private bucket, commit, auto-extract).
  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    // Ignore leave events bubbling from child nodes; only clear when the pointer
    // actually exits the card (prevents border flicker).
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    // WR-01: in-flight guard on the drop path. Dropping a second batch while the
    // first runInlineIngest is mid-commit would stage + commit it concurrently
    // (commit snapshots its own items and does not serialize), at best firing
    // onConfirmExtract twice, at worst racing two commits to the same boat. Block
    // the drop until the in-flight commit settles.
    if (upload.committing) return;
    // Mirror the picker guard (handleFilesSelected uses `?? []`). A drag that
    // carries no files (selected text, a URL, an OS-specific payload) has a
    // possibly-absent dataTransfer.files; the `?? []` keeps runInlineIngest's
    // `files.length === 0` early-return the single empty-case path.
    const files = Array.from(event.dataTransfer?.files ?? []);
    void runInlineIngest(files);
  };

  if (dismissed) return null;

  // Phase 45.5 (D-17) — once the inline commit hands off, replace the hero cards
  // with the transition copy so the dealer always knows extraction is next ("now
  // reading your documents…"). The Copilot reading-trail (right column) then
  // narrates the per-document steps. Fields fill live below.
  //
  // Phase 45.5.3 (CANVAS-15) — keep the inline MediaCard grid VISIBLE during
  // reading so the dealer sees the staged public/private placement + committed
  // gallery thumbnails while extraction runs (the grid returns null if both
  // sources are empty, so it adds no chrome).
  const handleDismiss = () => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch {
        // localStorage may be full / unavailable — still collapse locally
      }
    }
    setDismissed(true);
  };

  // Phase 45.5.3 (refresh) — the hero has exactly two visual modes, both keeping
  // the factory-packet dropzone (the SOLE add path) usable:
  //   • EMPTY / FRESH  → the PROMINENT dropzone under the "Start your listing"
  //     intro (+ a Skip that collapses the intro, not the dropzone).
  //   • WITH FILES / READING / DISMISSED → the same dropzone in a COMPACT form
  //     above the media grid; while a packet is read the "now reading…" status
  //     renders ALONGSIDE the compact dropzone (it is never replaced).
  // The grid self-hides when both sources are empty, so a fresh canvas shows no
  // grid chrome.
  const hasFiles =
    upload.items.length > 0 ||
    committedItems.length > 0 ||
    committedDocuments.length > 0 ||
    pendingImages.length > 0;
  const showFullIntro = !dismissed && !isReading && !hasFiles;

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      {showFullIntro ? (
        <>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              {/* Display 20px semibold per UI-SPEC §Typography */}
              <h2 className="text-xl font-semibold">Start your listing</h2>
              {/* Body 14px regular */}
              <p className="text-sm font-normal text-muted-foreground">
                Add the boat COO, trailer COO, dealer invoice, and bill of lading
                (PDFs or photos). The AI reads them and fills this listing for you
                — you just review and confirm.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="font-normal text-muted-foreground"
              aria-label="Dismiss hero ingest prompt"
            >
              <X className="h-4 w-4 me-1" />
              Skip — I&apos;ll fill manually
            </Button>
          </div>
          {/* The single, prominent factory-packet drop target. */}
          <FactoryPacketDropzone
            isDragging={isDragging}
            onClick={handleFactoryPacket}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        </>
      ) : (
        // COMPACT mode — the dropzone persists above the grid as the sole add
        // path. During reading the status line renders inside the SAME compact
        // dropzone (item 5: the reading status renders alongside/within it; the
        // drop target stays usable).
        <FactoryPacketDropzone
          compact
          isReading={isReading}
          isDragging={isDragging}
          onClick={handleFactoryPacket}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
      )}

      {/* CANVAS-08 — hidden picker behind the dropzone that feeds the inline
          ingest sequence. Single picker for BOTH the prominent and compact
          dropzone (one fileInputRef — the separate "Add more" picker is gone). */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={FACTORY_PACKET_ACCEPT}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleFilesSelected}
      />

      {/* CANVAS-15 — the v1-style MediaCard grid. Returns null until the first
          staged drop OR a committed gallery image / document exists, so it adds
          no chrome before then. Each staged card shows its public/private
          placement + a PRE-COMMIT reroute (onReroute=upload.reroute) and remove;
          each committed card shows an image thumbnail + a ⭐ set-featured +
          Delete (reusing the existing media endpoints).
          O-1 DEFERRAL: there is still NO dealer-facing REST verb to move a
          COMMITTED media item across the public (inventory_images) / private
          (inventory_documents) boundary — the Move control stays the pre-commit
          client reroute on STAGED items only; the safety default (private-by-
          default, server-enforced) holds regardless. WR-01: the grid + its
          cards' buttons are SIBLINGS of the dropzone <button> above, never
          descendants. */}
      <MediaCardGrid
        boatId={draftId}
        stagedItems={upload.items}
        committedItems={committedItems}
        committedPrimaryImageId={committedPrimaryImageId}
        committedDocuments={committedDocuments}
        pendingImages={pendingImages}
        onReroute={upload.reroute}
        onRemoveStaged={upload.remove}
        onDeleteDocument={onDeleteDocument}
        committing={upload.committing}
        commitError={upload.commitError}
      />
    </div>
  );
}

// =============================================================================
// FactoryPacketDropzone — Phase 45.5.3 (refresh). The SINGLE refined drop target
// and SOLE add path. Two forms via `compact`:
//   • default (prominent) — tall pad, large UploadCloud icon, "Drop your factory
//     packet" primary + "PDF or images — or browse" helper. Shown in the empty
//     state under the "Start your listing" intro.
//   • compact — a short single-row pad that persists above the media grid once
//     files exist / after the first commit / while reading, so the dealer always
//     has a drop target (the removed "Add more" button's job).
// While `isReading` the compact form swaps its helper copy for the "now reading
// your documents…" status WITHOUT disabling the drop target (the dealer may add
// more files mid-extraction; the backend extends the run via the epoch guard).
//
// It is a native <button> so click opens the hidden picker and Space/Enter work;
// drag handlers stage + commit via runInlineIngest. WR-01: it never contains a
// descendant <button> (the grid + its card buttons are siblings below).
// =============================================================================
function FactoryPacketDropzone({
  compact = false,
  isReading = false,
  isDragging,
  onClick,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  compact?: boolean;
  isReading?: boolean;
  isDragging: boolean;
  onClick: () => void;
  onDragOver: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDrop: (event: React.DragEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-label="Upload factory packet"
      className={cn(
        'group flex w-full items-center text-center',
        // Softer dashed border + muted fill at rest; primary-tinted highlight
        // while dragging. Smooth color transition + focus ring.
        'rounded-lg border-2 border-dashed transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging
          ? 'border-primary bg-primary/10'
          : 'border-border/70 bg-muted/20 hover:border-primary/70 hover:bg-accent/60',
        compact
          ? 'flex-row justify-center gap-3 px-4 py-3'
          : 'min-h-[180px] flex-col justify-center gap-3 p-8',
      )}
    >
      <UploadCloud
        className={cn(
          'shrink-0 transition-colors',
          compact ? 'size-6' : 'size-12',
          isDragging
            ? 'text-primary'
            : 'text-muted-foreground group-hover:text-foreground',
        )}
        role="img"
      />
      {compact ? (
        // One-line compact label; during reading it becomes the status line
        // (still a live drop target — item 5).
        <span className="text-sm font-normal text-foreground">
          {isDragging
            ? 'Drop your packet to upload'
            : isReading
              ? 'now reading your documents… — drop more anytime'
              : 'Drop more files here, or browse'}
        </span>
      ) : (
        <div className="space-y-1">
          {/* Primary copy 14px regular (typography lock — no 500-weight). */}
          <p className="text-sm font-normal text-foreground">
            {isDragging ? 'Drop your packet to upload' : 'Drop your factory packet'}
          </p>
          {/* Helper copy 14px regular, muted. */}
          <p className="text-sm font-normal text-muted-foreground">
            PDF or images — or browse
          </p>
        </div>
      )}
    </button>
  );
}

// =============================================================================
// MediaCardGrid — CANVAS-15. The v1-style media grid that REPLACES the flat
// UploadedFilesStrip. Maps the UNION of staged items (CombinedUploadItem, from
// useCombinedUpload) and committed items (TempMediaItem, derived in
// LiveFillCanvas from the useBoatMedia query) into <MediaCard>s in a responsive
// grid. The committed card whose `mediaId` equals `committedPrimaryImageId` is
// flagged primary (renders the ⭐ Featured badge + disables its own ⭐).
// Surfaces the batch-level commitError above the grid (lifted from
// UploadedFilesStrip). Returns null when BOTH sources are empty so it adds no
// chrome before the first drop.
//
// SELF-HEAL (Phase 45.5.3 review): committedItems + committedPrimaryImageId are
// query-derived, so a committed delete / set-primary mutation (fired by the
// MediaCard) invalidates the query and re-flows new values into this grid — the
// deleted card disappears and the ⭐ follows the new images[0] on the refetch.
// There is no featuredIndex to clamp and no reconciliation callback (the removed
// CR-01/CR-02 band-aid); matching the primary by mediaId means a refetch that
// reorders the list still flags exactly the right card.
//
// WR-01: this grid (and the per-card <button>s) is always a SIBLING of the hero
// dropzone <button>, never a descendant — the existing CanvasHeroIngest.test.tsx
// asserts the dropzone button has no descendant button.
// =============================================================================
function MediaCardGrid({
  boatId,
  stagedItems,
  committedItems,
  committedPrimaryImageId,
  committedDocuments,
  pendingImages,
  onReroute,
  onRemoveStaged,
  onDeleteDocument,
  committing,
  commitError,
}: {
  boatId: string | null;
  stagedItems: CombinedUpload['items'];
  committedItems: TempMediaItem[];
  committedPrimaryImageId?: number;
  committedDocuments: BoatDocument[];
  pendingImages: PendingBoatImage[];
  onReroute: (clientId: string) => void;
  onRemoveStaged: (clientId: string) => void;
  onDeleteDocument?: (mediaId: number) => void;
  committing: boolean;
  commitError?: string;
}) {
  // The grid only appears once the dealer has staged a file OR a committed
  // gallery image OR a committed DOCUMENT exists. The committedDocuments clause
  // is the fix for the original bug: a draft created by uploading ONLY a factory
  // packet (documents, no images) would otherwise return null here and show
  // nothing on resume.
  if (
    stagedItems.length === 0 &&
    committedItems.length === 0 &&
    committedDocuments.length === 0 &&
    pendingImages.length === 0
  ) {
    return null;
  }

  // Each source maps DIRECTLY into a typed MediaCard below (staged → committed
  // images → committed documents, so the resumed draft's uploaded files appear
  // in one unified grid). Mapping the source arrays inline keeps each `item`
  // narrowed to its concrete type (CombinedUploadItem / TempMediaItem /
  // BoatDocument) for the React `key`, rather than widening to the union.

  return (
    <div className="space-y-3" data-testid="media-card-grid">
      {commitError && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-md bg-destructive/20 px-4 py-2 text-sm text-destructive leading-normal"
        >
          <AlertCircle className="size-4" aria-hidden="true" />
          {commitError}
        </div>
      )}

      {/* #1219 — Sort again re-runs the boat's whole intake batch: one button for all held cards. */}
      {pendingImages.length > 0 && (
        <div className="flex items-center justify-between gap-2 text-[0.8125rem] text-muted-foreground">
          <span>{pendingImages.length === 1 ? '1 upload is not sorted yet.' : `${pendingImages.length} uploads are not sorted yet.`}</span>
          <SortHeldUploadsButton boatId={boatId} />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {/* #1219 — held canvas uploads: private until the classifier routes them. */}
        {pendingImages.map((item) => (
          <MediaCard
            key={`pending-image-${item.id}`}
            entry={{ kind: 'pending-image', item }}
            boatId={boatId}
          />
        ))}
        {stagedItems.map((item) => (
          <MediaCard
            key={`staged-${item.clientId}`}
            entry={{ kind: 'staged', item }}
            boatId={boatId}
            onReroute={onReroute}
            onRemoveStaged={onRemoveStaged}
            committing={committing}
          />
        ))}
        {committedItems.map((item) => (
          <MediaCard
            key={`committed-${item.clientId}`}
            entry={{ kind: 'committed', item }}
            boatId={boatId}
            // Match the primary by server mediaId (BoatMediaManager pattern:
            // images[0]). mediaId-based so a query refetch that reorders the list
            // still flags the right card; no index/featuredIndex bookkeeping.
            isPrimary={
              committedPrimaryImageId != null &&
              item.mediaId === committedPrimaryImageId
            }
          />
        ))}
        {/* Committed factory-packet documents — flat preview cards only.
            The CANVAS intentionally shows only the original uploaded document(s)
            with a preview. Packet splitting happens post-publish (after the boat
            leaves Draft on "Create Boat"); the split parent→children tree is
            presented on the boat-detail Documents tab, NOT in the creation canvas.
            onDeleteDocument fires useDocuments.destroy so the query refetch
            self-heals the card away. No isPrimary / reroute — documents are
            private, not gallery images. */}
        {committedDocuments.map((item) => (
          <MediaCard
            key={`committed-document-${item.id}`}
            entry={{ kind: 'committed-document', item }}
            boatId={boatId}
            onDeleteDocument={onDeleteDocument}
          />
        ))}
      </div>
    </div>
  );
}
